import { Pagination } from "../../conponents/Pagination";
import { AdminButton, AdminInput } from "./AdminControls";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Check, Search, X } from "lucide-react";
import styled from "styled-components";
import { GD } from "src/GD";
import { loadClientDirectory } from "src/services/clientDirectory";
import { TrafficEntityItem } from "./TrafficEntityItem";
import { isRecord, positiveId, unwrapAssignmentResponse, type TargetType } from "./trafficAssignmentsData";

export interface Target { id: number; name: string; status?: string; detail?: string; country?: string; clientId?: number }
const PAGE_SIZE = 20;
export const merchantTargets = (response: unknown, includeDeleted = false): Target[] => {
	const data = unwrapAssignmentResponse(response);
	const rows = Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.items) ? data.items : null;
	if (!rows || rows.some(item => !isRecord(item) || !positiveId(item.id))) throw new Error("invalidResponse");
	return rows.filter(item => includeDeleted || item.status !== "deleted").map(item => ({
		id: Number(item.id), name: String(item.name || item.domain || item.id),
		status: typeof item.status === "string" ? item.status : undefined,
		detail: typeof item.domain === "string" ? item.domain : undefined,
		country: typeof item.country === "string" ? item.country : undefined,
		clientId: positiveId(item.client_id) ?? undefined,
	}));
};

const Picker = styled.div`
	margin: 16px 0;
	.project-target, .choose-button { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; min-height: 38px; box-sizing: border-box; padding: 8px 12px; border: 1px solid #dce3ec; border-radius: 7px; background: #fff; color: #344054; text-align: left; font: inherit; font-size: 13px; }
	.project-target small, .choose-button small { color: #98a2b3; }
	.choose-button { cursor: pointer; }
	.choose-button.has-selection { min-height: 62px; justify-content: flex-start; }
	.choose-button:hover { border-color: #167ca6; }
	.choose-button[aria-invalid="true"] { border-color: #b42342; }
	.picker-error { margin: 7px 0 0; color: #b42342; font-size: 12px; }
`;

const Dialog = styled.div`
	position: fixed; inset: 0; z-index: 100002; display: grid; place-items: center; padding: 20px; box-sizing: border-box; background: #10182899;
	.dialog-card { display: flex; flex-direction: column; width: min(760px, 100%); max-height: min(720px, 90dvh); overflow: hidden; border-radius: 12px; background: #fff; box-shadow: 0 18px 50px #10182830; color: #344054; font: 13px/1.5 system-ui, sans-serif; }
	.dialog-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 22px; border-bottom: 1px solid #edf0f5; }
	.dialog-head h2 { margin: 0; font-size: 18px; color: #182230; }
	.close { display: grid; place-items: center; width: 30px; height: 30px; border: 0; border-radius: 6px; background: transparent; color: #667085; cursor: pointer; }
	.close:hover { background: #f2f4f7; }
	.search-area { padding: 16px 22px; }
	.search-control { position: relative; }
	.search-control svg { position: absolute; left: 12px; top: 11px; color: #98a2b3; pointer-events: none; }
	.search-control input { box-sizing: border-box; width: 100%; padding-left: 36px; }
	.list-scroll { min-height: 120px; overflow-y: auto; border-top: 1px solid #edf0f5; }
	.column-head, .target-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(100px, .5fr) 96px; align-items: center; gap: 16px; width: 100%; box-sizing: border-box; padding: 10px 22px; }
	.column-head { position: sticky; top: 0; z-index: 1; background: #fafbfe; color: #667085; font-size: 12px; }
	.target-row { min-height: 65px; border: 0; border-top: 1px solid #edf0f5; background: #fff; text-align: left; color: #344054; font: inherit; cursor: pointer; }
	.target-row:hover:not(:disabled), .target-row[aria-pressed="true"] { background: #f8fafc; }
	.target-row:disabled { cursor: default; }
	.detail { color: #667085; font-size: 12px; overflow-wrap: anywhere; }
	.assigned { color: #027a48; }
	.selected { color: #167ca6; }
	.picker-state { padding: 28px 22px; text-align: center; color: #667085; }
	.picker-state button { margin-left: 8px; }
	.dialog-footer { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 12px 22px; border-top: 1px solid #edf0f5; color: #667085; }
	.dialog-footer button { display: grid; place-items: center; min-height: 30px; width: 30px; padding: 0; }
	@media (max-width: 560px) {
		.column-head, .target-row { grid-template-columns: minmax(0, 1fr) 80px; gap: 8px; padding-inline: 16px; }
		.column-head span:nth-child(2), .detail { display: none; }
	}
`;

export const TrafficTargetPicker = ({ type, projectId, value, onChange, error, assignedIds, assignmentsLoading, dialogOnly = false, onClose }: {
	type: TargetType; projectId: number; value: string; onChange: (id: string) => void; error?: string; assignedIds: ReadonlySet<number>; assignmentsLoading: boolean;
	dialogOnly?: boolean; onClose?: () => void;
}) => {
	const { t } = useTranslation();
	const label = (key: string) => t(`trafficAssignments.${key}`);
	const title = t(`banRules.editor.options.${type}`);
	const dialogId = useId();
	const trigger = useRef<HTMLButtonElement>(null);
	const dialog = useRef<HTMLDivElement>(null);
	const searchInput = useRef<HTMLInputElement>(null);
	const [projectName, setProjectName] = useState("");
	const [open, setOpen] = useState(dialogOnly);
	const [items, setItems] = useState<Target[]>([]);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [retry, setRetry] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [selected, setSelected] = useState<Target | null>(null);
	useEffect(() => {
		if (type !== "project") return;
		let live = true;
		void GD.REQ_PROFILE.request().then(profile => { if (live) setProjectName(profile?.projects?.find(project => project.id === projectId)?.name ?? ""); }).catch(() => {});
		return () => { live = false; };
	}, [type, projectId]);
	useEffect(() => {
		if (!open || type === "project") return;
		const controller = new AbortController();
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => controller.abort());
		setLoading(true); setFailed(false); setItems([]);
		void (async () => {
			try {
				const rows = type === "client"
					? (await loadClientDirectory({ status: "active" }, { expectedProjectId: projectId, signal: controller.signal })).filter(item => item.status === "active").map(item => ({ id: item.id, name: item.name || String(item.id), status: item.status, detail: item.registration_number || item.country, country: item.country }))
					: await (async () => {
						const [response, clients] = await Promise.all([
							GD.REQ_API.request({ method: "clients/group/list", data: { status: "active" }, expectedProjectId: projectId, signal: controller.signal, headers: { ignoreBusy: "true" } }),
							loadClientDirectory({ status: "active" }, { expectedProjectId: projectId, signal: controller.signal }).catch(() => []),
						]);
						const countries = new Map(clients.map(client => [client.id, client.country]));
						return merchantTargets(response).filter(row => row.status === "active").map(row => ({ ...row, country: row.country || countries.get(row.clientId ?? 0) }));
					})();
				if (!controller.signal.aborted) { setItems(rows); setLoading(false); }
			} catch { if (!controller.signal.aborted) { setFailed(true); setLoading(false); } }
		})();
		return () => { controller.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, [open, type, projectId, retry]);
	useEffect(() => {
		if (!open) return;
		const previous = document.activeElement;
		searchInput.current?.focus();
		return () => { if (previous instanceof HTMLElement && previous.isConnected) previous.focus(); };
	}, [open]);
	const close = () => { setOpen(false); trigger.current?.focus(); onClose?.(); };
	if (type === "project") return <Picker><div className="project-target">{projectName || title}<small>#{projectId}</small>{assignedIds.has(projectId) && <small>{label("alreadyAssigned")}</small>}</div>{error && <p className="picker-error" role="alert">{error}</p>}</Picker>;
	const term = search.trim().toLocaleLowerCase();
	const matches = items.filter(item => String(item.id).includes(term) || item.name.toLocaleLowerCase().includes(term));
	const totalPages = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
	const visiblePage = Math.min(page, totalPages - 1);
	const visible = matches.slice(visiblePage * PAGE_SIZE, (visiblePage + 1) * PAGE_SIZE);
	const current = selected?.id === Number(value) ? selected : items.find(item => item.id === Number(value));
	const choose = (item: Target) => {
		const next = !dialogOnly && Number(value) === item.id ? null : item;
		setSelected(next);
		onChange(next ? String(next.id) : "");
		close();
	};
	return <>
		{!dialogOnly && <Picker><AdminButton variant="secondarySmall" ref={trigger} type="button" className={`choose-button${value ? " has-selection" : ""}`} aria-haspopup="dialog" aria-label={value ? `${label("changeTarget")}: ${current?.name || title}` : label("choose")} aria-invalid={!!error} onClick={() => { setSearch(""); setPage(0); setOpen(true); }}>{value ? <TrafficEntityItem type={type} id={Number(value)} name={current?.name || title} status={current?.status} country={current?.country} /> : <><span>{label("choose")}</span><small>{label("chooseList")}</small></>}</AdminButton>{error && <p className="picker-error" role="alert">{error}</p>}</Picker>}
		{open && createPortal(<Dialog role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
			<div ref={dialog} className="dialog-card" role="dialog" aria-modal="true" aria-labelledby={dialogId} onKeyDown={event => {
				event.stopPropagation();
				if (event.key === "Escape") { event.preventDefault(); close(); }
				if (event.key !== "Tab") return;
				const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') ?? []);
				const first = controls[0], last = controls[controls.length - 1];
				if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
				else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
			}}>
				<div className="dialog-head"><h2 id={dialogId}>{label("choose")} {title}</h2><AdminButton variant="secondarySmall" type="button" className="close" aria-label={label("close")} onClick={close}><X size={18} /></AdminButton></div>
				<div className="search-area"><div className="search-control"><Search size={16} /><AdminInput ref={searchInput} type="search" aria-label={label("searchTarget")} placeholder={label("searchTarget")} value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} /></div></div>
				<div className="list-scroll" aria-busy={loading || assignmentsLoading}>
					<div className="column-head"><span>{title}</span><span>{type === "merchant" ? label("domain") : label("clientDetails")}</span><span>{label("state")}</span></div>
					{loading || assignmentsLoading ? <div className="picker-state" role="status">{t("common.loading")}</div> : failed ? <div className="picker-state" role="alert">{label("targetsError")} <AdminButton variant="secondarySmall" type="button" onClick={() => setRetry(count => count + 1)}>{t("common.refresh")}</AdminButton></div> : !visible.length ? <div className="picker-state">{label("noTargets")}</div> : visible.map(item => {
						const assigned = assignedIds.has(item.id), active = Number(value) === item.id;
						return <AdminButton variant="empty" type="button" className="target-row" aria-pressed={active} disabled={assigned} key={item.id} onClick={() => choose(item)}>
							<TrafficEntityItem type={type} id={item.id} name={item.name} status={item.status} country={item.country} />
							<span className="detail">{item.detail || "—"}</span>
							<span className={assigned ? "assigned" : active ? "selected" : ""}>{assigned ? <><Check size={13} /> {label("alreadyAssigned")}</> : active ? <><Check size={13} /> {label("selected")}</> : ""}</span>
						</AdminButton>;
					})}
				</div>
				<div className="dialog-footer"><Pagination pagination={{ page: visiblePage, perPage: PAGE_SIZE, total: matches.length, totalPages }} onPageChange={setPage} /></div>
			</div>
		</Dialog>, document.body)}
	</>;
};
